/*
 * Copyright 2026 Sony Corporation
 */

import { Box, CircularProgress, Typography, alpha } from '@mui/material';
import { useEffect, useRef } from 'react';

import { ConfirmBar } from './ConfirmBar';
import { OpsDiff } from './OpsDiff';
import type { ChatMessage } from '../../hooks/use-agent';

export interface MessageListProps {
  messages: readonly ChatMessage[];
  busy: boolean;
  /** The turn being written, before it becomes a message. */
  streaming?: string;
  onApply: (messageId: string) => void;
  onDiscard: (messageId: string) => void;
}

const STATUS: Record<'applied' | 'discarded', string> = {
  applied: 'Applied',
  discarded: 'Discarded',
};

export const MessageList = ({ messages, busy, streaming, onApply, onDiscard }: MessageListProps) => {
  const scroller = useRef<HTMLDivElement>(null);

  // The newest turn is the one being read, and a turn can be several lines of
  // diff long, so the thread follows it rather than staying where it was.
  useEffect(() => {
    const element = scroller.current;
    if (element) {
      element.scrollTop = element.scrollHeight;
    }
  }, [messages, busy, streaming]);

  return (
    <Box ref={scroller} className="flex flex-1 flex-col gap-3 overflow-y-auto px-3 py-2">
      {messages.map((message) =>
        message.role === 'user' ? (
          <Box
            key={message.id}
            className="self-end rounded px-2 py-1"
            sx={(theme) => ({ bgcolor: alpha(theme.palette.primary.main, 0.16), maxWidth: '85%' })}
          >
            <Typography variant="body2">{message.text}</Typography>
          </Box>
        ) : (
          <Box key={message.id}>
            {/* Plain text, never Markdown or HTML: model output is untrusted
                input, and there is no path from it to `innerHTML`. */}
            <Typography variant="body2" className="whitespace-pre-wrap">
              {message.text}
            </Typography>

            {message.ops && message.ops.length > 0 && (
              <>
                <OpsDiff ops={message.ops} />
                {message.status === 'pending' ? (
                  <ConfirmBar
                    ops={message.ops.length}
                    onApply={() => onApply(message.id)}
                    onDiscard={() => onDiscard(message.id)}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {STATUS[message.status ?? 'applied']}
                  </Typography>
                )}
              </>
            )}

            {message.errors?.map((reason) => (
              <Typography key={reason} variant="caption" color="error" className="block">
                {reason}
              </Typography>
            ))}
          </Box>
        )
      )}

      {/* The turn arriving. It becomes a message of its own once it ends, so
          this is the same text twice for one render and never for longer. */}
      {streaming && (
        <Typography variant="body2" className="whitespace-pre-wrap">
          {streaming}
        </Typography>
      )}

      {busy && !streaming && (
        <Box className="flex items-center gap-2">
          <CircularProgress size={12} />
          <Typography variant="caption" color="text.secondary">
            Thinking…
          </Typography>
        </Box>
      )}
    </Box>
  );
};
